<template>

    <div class="flex flex-col gap-1 relative my-4">
      <select id="locker_country_id"
              v-model="shipping.country_id"
              :disabled="inProgress"
              name="locker_country_id"
              @change="onSelectCountry"
              class="w-full h-[52px] text-[16px] p-[16px] font-light leading-[20px] text-charcoal border  rounded-[8px] outline-none transition-all focus-within:border-lime-green hover:border-lime-green border-light-gray/25 cursor-pointer"
      >
        <option :value="null" disabled selected>{{ in18['attribute_shipping_country_option'] }}</option>
        <option v-for="country in countries"
                :key="country.id"
                :value="country.id">
          {{ country.title }}
        </option>
      </select>
      <span v-if="errors['shipping.country_id']" class=" text-[12px] text-[#eb2635] text-left">*{{ errors['shipping.country_id']?.[0]}}</span>
    </div>

  <div class="flex flex-col gap-1 relative mb-4">
    <!-- Pickup Points Select (Hidden initially) -->
    <div class="form__group">
      <select
          id="locker_identifier_id"
          :disabled="inProgress"
          class="w-full h-[52px] text-[16px] p-[16px] font-light leading-[20px] text-charcoal border  rounded-[8px] outline-none transition-all focus-within:border-lime-green border-light-gray/25"
          name="locker_identifier_id"
      >
      </select>
      <validation-error :errors="errors['shipping.identifier']"/>
    </div><!-- End Pickup Points Select -->

      <div class="flex flex-col gap-1 relative overflow-hidden mt-4">
        <label class=" leading-[14px] font-light absolute left-[17px] transition-all pointer-events-none" :class="[shipping.message ? 'top-[10px] text-[12px] text-lime-green' : 'top-[19px] text-[16px] text-gray']" for="locker_message">
          {{ in18['attribute_shipping_message_option'] }}
        </label>
        <textarea
            id="locker_message"
            v-model="shipping.message"
            :class="[errors['shipping.message'] ? 'border-[#eb2635]' : 'focus-within:border-lime-green border-light-gray/25', shipping.message ? 'pt-[30px]' : 'pt-[16px]' ]"
            :disabled="inProgress"
            class="min-h-[100px] text-[16px] p-[16px] font-light leading-[20px] text-charcoal border rounded-[8px] outline-none transition-all"
            name="shipping_message"
            @input="clearValidationError('shipping.message')"
        />
        <span v-if="errors['shipping.message']?.length" class=" text-[12px] text-[#eb2635] text-left">*{{ errors['shipping.message']?.[0]}}</span>
      </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { all } from 'country-codes-list';
import ValidationError from '../../../features/ValidationError.vue';

// Define the props
const props = defineProps({
  shippingMethod: {
    type: Object,
    required: true,
  },
  shipping: {
    type: Object,
    required: true,
  },
  errors: {
    type: Object,
    required: true,
  },
  in18: {
    type: Object,
    default: () => ({}),
  },
  inProgress: {
    type: Boolean,
    default: false,
  },
  addresses: {
    type: Array,
  }
});

// Define the emits
const emit = defineEmits([
  'update-cart',
  'country-selected',
  'update-in-progress',
  'update-error',
  'update-shipping-address',
]);

// Reactive state variables
const countries = ref([]);
const points = ref([]);
const selectedShippingAddress = ref(null);

// Fetch available countries
function getAvailableCountries() {
  axios
    .get(`/api/shipping/${props.shippingMethod.id}/countries`)
    .then((response) => {
      countries.value = response.data.countries;
    })
    .catch((error) => {
      console.error('Error fetching countries:', error);
    });
}

// Validate Zip Code
function validateZipCode() {
  emit('update-in-progress', true);

  axios
    .post(
      `/api/countries/${props.shipping.country_id}/validate-zip-code`,
      { shipping: props.shipping }
    )
    .then((response) => {
      emit('update-cart', response.data.cart);
      ga_add_shipping_info_event(response.data.cart); // Ensure this function is available
    })
    .catch((error) => {
      window.toastr.error(error.response.data.message);
      emit(
        'update-error',
        'shipping.zip',
        error.response.data.errors['shipping.zip']
      );
    })
    .finally(() => {
      emit('update-in-progress', false);
    });
}

// Reset shipping data
function resetShippingData() {
  props.shipping.zip = '';
  props.shipping.city = '';
  props.shipping.address = '';
  props.shipping.identifier = null;

  points.value = [];
  $('#locker_identifier_id').empty();
}

// Handle country selection
function onSelectCountry() {
  resetShippingData();
  emit('country-selected', props.shippingMethod.id, props.shipping);
  getPoints();
}

// Fetch and populate pickup points based on the selected country
function getPoints() {
  const countryID = props.shipping.country_id;
  const locker_el = $('#locker_identifier_id');

  locker_el.off();

  locker_el
    .select2({
      placeholder: props.in18['attribute_shipping_locker_option'],
      width: '100%',
      allowClear: false,
      debug: true,
      ajax: {
        delay: 800,
        url: `/api/shipping/${props.shippingMethod.id}/pickup-points`,
        data: function (params) {
          return {
            search: params.term,
            country_id: countryID,
          };
        },
        type: 'GET',
        dataType: 'json',
        processResults: function (response) {
          points.value = response.points; // Update the points array
          return {
            results: response.points.map(function (item) {
              return {
                id: item.id,
                text: item.full_address,
                address: item.address,
                zip: item.zip,
                city: item.city,
                country_id: item.country_id,
                identifier: item.identifier,
              };
            }),
          };
        },
        cache: true,
      },
    })
    .on('select2:select', (e) => {
      // Update shipping data based on the selected pickup point
      props.shipping.zip = e.params.data.zip;
      props.shipping.city = e.params.data.city;
      props.shipping.address = e.params.data.address;
      props.shipping.country_id = e.params.data.country_id;
      props.shipping.identifier = e.params.data.identifier;

      // Call validateZipCode when the selection changes
      validateZipCode();
    });
}

// Initialize available countries when the component is created
getAvailableCountries();

onMounted(() => {
  getPoints();
});
</script>

<style>
.select2-selection.select2-selection--single{
  @apply w-full h-[52px] text-[16px] font-light leading-[20px] text-charcoal border  rounded-[8px] outline-none transition-all focus-within:border-lime-green hover:border-lime-green border-light-gray/25;
}

.select2-selection__arrow{
  @apply hidden;
}

.select2-selection.select2-selection--single {
  padding-right: 2rem; /* Adjust to match text padding */
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23333' d='M5 6L0 0h10L5 6z'/%3E%3C/svg%3E"); /* Smaller arrow icon */
  background-position: right 16px center;
  background-repeat: no-repeat;
}
</style>
